'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from 'next-auth/react'
import { useConnections, useConnect, useDisconnect, useConfig } from 'wagmi'
import type { Connector } from 'wagmi'
import { Button } from './ui/Button'
import { ChevronIcon } from './icons/ChevronIcon'
import { WalletIcon } from './icons/WalletIcon'

const wcProjectId = process.env.NEXT_PUBLIC_WC_PROJECT_ID ?? ''

// ─── Connector icon helper ──────────────────────────────────────────────────

function ConnectorIcon({ name, size = 20 }: { name: string; size?: number }) {
  const lower = name.toLowerCase()
  if (lower.includes('metamask')) return <MetaMaskIcon size={size} />
  if (lower.includes('kraken')) return <KrakenIcon size={size} />
  if (lower.includes('walletconnect')) return <WalletConnectIcon size={size} />
  return <WalletIcon size={size} />
}

function WalletConnectIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 300 185"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M61.4 36.7c48.9-47.9 128.3-47.9 177.2 0l5.9 5.8a6.1 6.1 0 0 1 0 8.7l-20.1 19.7a3.2 3.2 0 0 1-4.4 0l-8.1-7.9c-34.1-33.4-89.4-33.4-123.5 0l-8.7 8.5a3.2 3.2 0 0 1-4.4 0L55.2 51.8a6.1 6.1 0 0 1 0-8.7l6.2-6.4ZM280 77.5l17.9 17.5a6.1 6.1 0 0 1 0 8.7l-80.7 79a6.3 6.3 0 0 1-8.9 0L150 125.2a1.6 1.6 0 0 0-2.2 0L89.6 182.7a6.3 6.3 0 0 1-8.9 0L.1 103.7a6.1 6.1 0 0 1 0-8.7L18 77.5a6.3 6.3 0 0 1 8.9 0l58.3 57.1a1.6 1.6 0 0 0 2.2 0l58.2-57.1a6.3 6.3 0 0 1 8.9 0l58.3 57.1a1.6 1.6 0 0 0 2.2 0l58.2-57.1a6.3 6.3 0 0 1 8.8 0Z"
        fill="#3B99FC"
      />
    </svg>
  )
}

// ─── Wallet definitions ─────────────────────────────────────────────────────

const WALLETS = [
  {
    id: 'metamask',
    connectorId: 'metaMask',
    name: 'MetaMask',
    icon: MetaMaskIcon,
    description: 'Connect using MetaMask browser extension',
  },
  {
    id: 'kraken',
    connectorId: 'krakenWallet',
    name: 'Kraken Wallet',
    icon: KrakenIcon,
    description: 'Connect using Kraken Wallet extension',
  },
  ...(wcProjectId
    ? [
        {
          id: 'walletconnect',
          connectorId: 'walletConnect',
          name: 'WalletConnect',
          icon: WalletConnectIcon,
          description: 'Scan QR with any mobile wallet',
        },
      ]
    : []),
]

// ─── Helpers ────────────────────────────────────────────────────────────────

const LORE_CONNECTOR_KEY = 'lore:wallet:savedConnectorId'
const LORE_DEFAULT_WALLET_KEY = 'lore:wallet:defaultAddress'

// ─── Extracted sub-components (must be at module scope) ─────────────────────

interface WalletPickerProps {
  inModal: boolean
  isConnected: boolean
  connecting: string | null
  connectError: string | null
  onConnect: (walletId: string) => void
  onBack: () => void
  onClearError: () => void
}

function WalletPicker({
  inModal,
  isConnected,
  connecting,
  connectError,
  onConnect,
  onBack,
  onClearError,
}: WalletPickerProps) {
  return (
    <div>
      {isConnected && (
        <button
          onClick={onBack}
          aria-label="Back to connected wallets"
          className="flex items-center gap-1 font-mono text-[12px] tracking-[1px] text-mist dark:text-cream/30 hover:text-carolina-dark dark:hover:text-carolina mb-3 transition-colors"
        >
          &larr; BACK
        </button>
      )}
      <p
        className={`font-mono text-[16px] tracking-[3px] mb-3 px-1 ${inModal ? 'text-cream/30' : 'text-mist dark:text-cream/30'}`}
      >
        SELECT WALLET
      </p>
      <div data-part="wallet-list" className="flex flex-col gap-2">
        {WALLETS.map((wallet) => (
          <button
            key={wallet.id}
            data-part="wallet-option"
            data-wallet={wallet.id}
            onClick={() => {
              onClearError()
              onConnect(wallet.id)
            }}
            disabled={connecting === wallet.id}
            aria-busy={connecting === wallet.id}
            aria-label={`Connect ${wallet.name}`}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left group
              ${
                inModal
                  ? 'border-carolina/10 hover:border-carolina/30'
                  : 'border-carolina/20 dark:border-carolina/10 hover:border-carolina/40 dark:hover:border-carolina/30'
              }
              hover:bg-carolina/5`}
          >
            <div className="w-9 h-9 rounded-lg bg-paper dark:bg-navy/60 border border-carolina/20 dark:border-carolina/10 flex items-center justify-center shrink-0">
              <wallet.icon size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[16px] tracking-[1px] text-ink/80 dark:text-cream/80 group-hover:text-carolina-dark dark:group-hover:text-carolina transition-colors">
                {connecting === wallet.id ? 'CONNECTING...' : wallet.name.toUpperCase()}
              </p>
              <p className="text-[16px] text-mist dark:text-cream/30 mt-0.5 truncate">
                {wallet.description}
              </p>
            </div>
          </button>
        ))}
      </div>
      {connectError ? (
        <p className="font-mono text-[13px] tracking-[1px] text-pink-dark dark:text-pink text-center mt-3">
          {connectError}
        </p>
      ) : (
        <p className="font-mono text-[16px] text-mist dark:text-cream/20 text-center mt-3">
          Make sure your wallet extension is installed
        </p>
      )}
    </div>
  )
}

interface ConnectedContentProps {
  inModal: boolean
  showSelector: boolean
  connections: ReturnType<typeof useConnections>
  sortedConnections: ReturnType<typeof useConnections>
  connecting: string | null
  connectError: string | null
  onConnect: (walletId: string) => void
  onDisconnect: (connector: Connector) => void
  onShowSelector: () => void
  onHideSelector: () => void
  onClearError: () => void
}

function ConnectedContent({
  inModal,
  showSelector,
  connections,
  sortedConnections,
  connecting,
  connectError,
  onConnect,
  onDisconnect,
  onShowSelector,
  onHideSelector,
  onClearError,
}: ConnectedContentProps) {
  if (showSelector) {
    return (
      <WalletPicker
        inModal={inModal}
        isConnected={true}
        connecting={connecting}
        connectError={connectError}
        onConnect={onConnect}
        onBack={onHideSelector}
        onClearError={onClearError}
      />
    )
  }

  return (
    <>
      <p
        className={`font-mono text-[16px] tracking-[2px] mb-3 px-1 ${inModal ? 'text-cream/30' : 'text-mist dark:text-cream/30'}`}
      >
        {connections.length > 1 ? `WALLETS (${connections.length})` : 'CONNECTED'}
      </p>
      <div className="flex flex-col gap-2 mb-3">
        {sortedConnections.map((conn) => (
          <div
            key={conn.connector.id}
            className={`p-3 rounded-lg border ${inModal ? 'border-carolina/10' : 'border-carolina/20 dark:border-carolina/10'} bg-carolina/[0.03]`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <ConnectorIcon name={conn.connector.name} size={14} />
              <span className="font-mono text-[11px] tracking-[1px] text-mist dark:text-cream/40">
                {conn.connector.name.toUpperCase()}
              </span>
            </div>
            <p className="font-mono text-[13px] text-ink/70 dark:text-cream/70 break-all mb-2">
              {conn.accounts[0]}
            </p>
            <Button
              variant="danger"
              size="sm"
              onClick={() => onDisconnect(conn.connector)}
              className="w-full"
            >
              DISCONNECT
            </Button>
          </div>
        ))}
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={onShowSelector}
        className={`w-full ${inModal ? 'border-carolina/20 text-cream/60 hover:border-carolina/40 hover:text-cream' : ''}`}
      >
        + ADD WALLET
      </Button>
    </>
  )
}

// ─── Main Component ─────────────────────────────────────────────────────────

interface WalletConnectProps {
  fluid?: boolean
}

export default function WalletConnect({ fluid }: WalletConnectProps) {
  const [open, setOpen] = useState(false)
  const [showSelector, setShowSelector] = useState(false)
  const [connecting, setConnecting] = useState<string | null>(null)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const reconnectAttempted = useRef(false)

  const { data: session } = useSession()
  const config = useConfig()
  const connections = useConnections()
  const { connect } = useConnect()
  const { disconnect } = useDisconnect()

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true))
  }, [])

  // Close on outside click / Escape
  useEffect(() => {
    const onMouse = (e: MouseEvent) => {
      const target = e.target as Node
      if (!modalRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setOpen(false)
        setShowSelector(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setShowSelector(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onMouse)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouse)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  // Auto-open wallet picker when returning from sign-in via CONNECT button
  useEffect(() => {
    if (!mounted || !session) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('openWallet') !== '1') return
    params.delete('openWallet')
    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '')
    window.history.replaceState({}, '', newUrl)
    requestAnimationFrame(() => setOpen(true))
  }, [mounted, session])

  // Auto-reconnect when session becomes available (after sign-in)
  useEffect(() => {
    if (!mounted) return
    if (!session?.user) {
      reconnectAttempted.current = false
      return
    }
    if (connections.length > 0 || reconnectAttempted.current) return
    reconnectAttempted.current = true

    const savedId = localStorage.getItem(LORE_CONNECTOR_KEY)
    if (!savedId) return
    const wallet = WALLETS.find((w) => w.id === savedId)
    if (!wallet) return
    const connector = config.connectors.find((c) => c.id === wallet.connectorId)
    if (!connector) return

    try {
      connect({ connector })
    } catch {
      // ignore — extension may not be available
    }
  }, [mounted, session, connections.length, connect, config.connectors])

  const handleConnect = useCallback(
    (walletId: string) => {
      const wallet = WALLETS.find((w) => w.id === walletId)
      if (!wallet) return
      const connector = config.connectors.find((c) => c.id === wallet.connectorId)
      if (!connector) return
      setConnecting(walletId)
      connect(
        { connector },
        {
          onSuccess: () => {
            setConnectError(null)
            localStorage.setItem(LORE_CONNECTOR_KEY, walletId)
            setOpen(false)
            setShowSelector(false)
          },
          onError: (error) => {
            const msg = (error.message ?? '').toLowerCase()
            if (
              msg.includes('connection request reset') ||
              msg.includes('user rejected') ||
              msg.includes('user closed')
            ) {
              setConnectError('CONNECTION CANCELLED')
            } else {
              setConnectError('CONNECTION FAILED — PLEASE TRY AGAIN')
            }
          },
          onSettled: () => setConnecting(null),
        }
      )
    },
    [config.connectors, connect]
  )

  const handleExplicitDisconnect = useCallback(
    (connector: Connector) => {
      disconnect({ connector })
      if (connections.length <= 1) {
        localStorage.removeItem(LORE_CONNECTOR_KEY)
      }
      setOpen(false)
      setShowSelector(false)
    },
    [connections.length, disconnect]
  )

  const handleClearError = useCallback(() => setConnectError(null), [])
  const handleShowSelector = useCallback(() => setShowSelector(true), [])
  const handleHideSelector = useCallback(() => setShowSelector(false), [])
  const handleClose = useCallback(() => {
    setOpen(false)
    setShowSelector(false)
  }, [])

  const isConnected = mounted && connections.length > 0

  const sortedConnections = isConnected
    ? [...connections].sort((a, b) => {
        const def = mounted ? (localStorage.getItem(LORE_DEFAULT_WALLET_KEY) ?? '') : ''
        const aIsDefault = a.accounts[0]?.toLowerCase() === def.toLowerCase()
        const bIsDefault = b.accounts[0]?.toLowerCase() === def.toLowerCase()
        return aIsDefault === bIsDefault ? 0 : aIsDefault ? -1 : 1
      })
    : connections

  // ── Connected render ──────────────────────────────────────────────────────

  if (isConnected) {
    return (
      <div
        data-component="WalletConnect"
        data-state="connected"
        className={`relative${fluid ? ' flex-1' : ''}`}
        ref={modalRef}
      >
        <button
          ref={triggerRef}
          data-part="connected-btn"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-haspopup="true"
          className={`flex items-center gap-2 px-3 border border-carolina-dark/30 dark:border-carolina/30 rounded
            font-mono text-[16px] tracking-[1px] text-carolina-dark dark:text-carolina hover:border-carolina-dark dark:hover:border-carolina transition-all
            ${fluid ? 'w-full justify-center py-3' : 'py-1.5'}`}
        >
          <span
            data-part="connected-dot"
            className="w-2 h-2 rounded-full bg-carolina animate-pulse"
          />
          WALLETS
          <ChevronIcon size={10} />
        </button>

        {open && !fluid && (
          <div
            data-part="connected-dropdown"
            className="absolute top-full right-0 mt-2 bg-white dark:bg-dark border border-carolina/25 dark:border-carolina/20 rounded-xl p-3 shadow-xl min-w-64 z-50"
          >
            <ConnectedContent
              inModal={false}
              showSelector={showSelector}
              connections={connections}
              sortedConnections={sortedConnections}
              connecting={connecting}
              connectError={connectError}
              onConnect={handleConnect}
              onDisconnect={handleExplicitDisconnect}
              onShowSelector={handleShowSelector}
              onHideSelector={handleHideSelector}
              onClearError={handleClearError}
            />
          </div>
        )}

        {open &&
          fluid &&
          createPortal(
            <>
              <div className="fixed inset-0 z-[199] bg-black/50" onClick={handleClose} />
              <div
                ref={dropdownRef}
                data-part="connected-dropdown"
                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-w-sm max-h-[80vh] overflow-y-auto bg-white dark:bg-dark border border-carolina/25 dark:border-carolina/20 rounded-xl p-4 shadow-xl z-[200]"
              >
                <button
                  onClick={handleClose}
                  aria-label="Close wallet panel"
                  className="absolute top-3 right-3 text-white/60 hover:text-white text-[2rem] leading-none"
                >
                  &times;
                </button>
                <ConnectedContent
                  inModal={true}
                  showSelector={showSelector}
                  connections={connections}
                  sortedConnections={sortedConnections}
                  connecting={connecting}
                  connectError={connectError}
                  onConnect={handleConnect}
                  onDisconnect={handleExplicitDisconnect}
                  onShowSelector={handleShowSelector}
                  onHideSelector={handleHideSelector}
                  onClearError={handleClearError}
                />
              </div>
            </>,
            document.body
          )}
      </div>
    )
  }

  // ── Disconnected render ───────────────────────────────────────────────────

  return (
    <div
      data-component="WalletConnect"
      data-state="disconnected"
      className={`relative${fluid ? ' flex-1' : ''}`}
      ref={modalRef}
    >
      <button
        ref={triggerRef}
        data-part="connect-btn"
        onClick={() => {
          if (!session) {
            // Connecting a wallet needs a session, but which way the user signs
            // in is their choice — send them to the auth page and come back to
            // this page with the wallet modal open.
            const params = new URLSearchParams(window.location.search)
            params.set('openWallet', '1')
            const callbackUrl = `${window.location.pathname}?${params.toString()}`
            window.location.assign(`/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`)
          } else {
            setOpen(!open)
          }
        }}
        aria-expanded={session ? open : undefined}
        aria-haspopup={session ? 'true' : undefined}
        className={`flex items-center gap-2 px-3 border border-yellow-dark/30 dark:border-yellow/30 rounded
          font-mono text-[16px] tracking-[1px] text-yellow-dark dark:text-yellow/80 hover:border-yellow-dark dark:hover:border-yellow hover:text-yellow-dark dark:hover:text-yellow transition-all
          ${fluid ? 'w-full justify-center py-3' : 'py-1.5'}`}
      >
        <WalletIcon size={13} />
        CONNECT
      </button>

      {open && !fluid && (
        <div
          data-part="wallet-dropdown"
          className="absolute top-full right-0 mt-2 bg-white dark:bg-dark border border-carolina/25 dark:border-carolina/20 rounded-xl p-3 shadow-xl min-w-64 z-50"
        >
          <WalletPicker
            inModal={false}
            isConnected={false}
            connecting={connecting}
            connectError={connectError}
            onConnect={handleConnect}
            onBack={handleHideSelector}
            onClearError={handleClearError}
          />
        </div>
      )}

      {open &&
        fluid &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[199] bg-black/50" onClick={() => setOpen(false)} />
            <div
              ref={dropdownRef}
              data-part="wallet-dropdown"
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-w-sm h-[60vh] overflow-y-auto bg-white dark:bg-dark border border-carolina/25 dark:border-carolina/20 rounded-xl p-4 shadow-xl z-[200]"
            >
              <button
                onClick={() => setOpen(false)}
                aria-label="Close wallet panel"
                className="absolute top-3 right-3 text-white/60 hover:text-white text-[2rem] leading-none"
              >
                &times;
              </button>
              <WalletPicker
                inModal={true}
                isConnected={false}
                connecting={connecting}
                connectError={connectError}
                onConnect={handleConnect}
                onBack={handleHideSelector}
                onClearError={handleClearError}
              />
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function MetaMaskIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 318 318"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon
        points="274.1,35.5 174.6,109.4 193,65.8"
        fill="#E2761B"
        stroke="#E2761B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points="43.9,35.5 142.7,110.1 125,65.8"
        fill="#E4761B"
        stroke="#E4761B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points="238.3,206.8 211.8,247.4 268.5,263 284.8,207.7"
        fill="#E4761B"
        stroke="#E4761B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points="33.2,207.7 49.5,263 106.2,247.4 79.7,206.8"
        fill="#E4761B"
        stroke="#E4761B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points="103,138.2 87.3,162.1 143.6,164.6 141.7,103.9"
        fill="#E4761B"
        stroke="#E4761B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points="215,138.2 176.3,103.2 174.6,164.6 230.7,162.1"
        fill="#E4761B"
        stroke="#E4761B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function KrakenIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="100" height="100" rx="20" fill="#5741D9" />
      <text
        x="50"
        y="68"
        textAnchor="middle"
        fontSize="52"
        fontWeight="bold"
        fill="white"
        fontFamily="sans-serif"
      >
        K
      </text>
    </svg>
  )
}
